//External libs
import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/core/services/service';
import ReadingsModel from '../models/reading.model';
import FarmSiteModel from '../models/farm-site.model';

/**
 * @service ReadingsService
 * @description Service for managing water quality readings and environmental parameters within the aquaculture system.
 * This service acts as the central hub for retrieving, analyzing, and summarizing data collected from ponds.
 * It handles a wide range of parameters including pH, Dissolved Oxygen (DO), Salinity, Temperature, Alkalinity, and Turbidity.
 *
 * The service interacts with the `/aqu/readings/` backend endpoint and provides functionality for:
 * 1. **Raw Data Retrieval**: Fetching individual reading records based on date, pond, or culture cycle.
 * 2. **Summarization**: Aggregating data to provide high-level summaries for dashboards (e.g., average pH across a farm).
 * 3. **Location-Based Analysis**: Retrieving parameter data grouped by location for spatial analysis.
 * 4. **Graphing Data**: Fetching historical data points formatted for chart visualization.
 *
 * **Key Responsibilities:**
 * - Fetching daily readings for specific ponds.
 * - Generating summaries for critical water quality parameters.
 * - Supporting dashboard widgets with aggregated data.
 * - Managing HTTP request lifecycles to prevent memory leaks.
 *
 * @feature 1. Retrieve readings by date, pond, or farm site.
 * @feature 2. Parameter-specific summaries (pH, DO, Salinity, etc.).
 * @feature 3. Location-based parameter distribution.
 * @feature 4. Robust management of concurrent HTTP requests.
 *
 * @architecture
 * Extends base Service<ReadingsModel> providing REST API integration.
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 *
 * @service-endpoint /aqu/readings/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class ReadingsService extends Service<ReadingsModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * Used to maintain references to active Observables so they can be unsubscribed
   * when the service is destroyed or when cancellation is requested.
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @type {string}
   * @description Base URL for reading-specific operations (if different from serviceUrl).
   * Currently declared but not explicitly initialized in the constructor beyond the super call.
   */
  public readingurl: string;

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient.
   *
   * Initializes the service by calling the parent Service constructor with the
   * HttpClient instance and the specific API endpoint path (`/aqu/readings/`).
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests to the backend.
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/aqu/readings/');
  }

  /**
   * @method getReadingsByDate
   * @async
   * @description Fetch readings based on date criteria provided in the model.
   *
   * @param {ReadingsModel} readings - Model containing date filters (e.g., fromDate, toDate).
   * @returns {Promise<Array<ReadingsModel>>} Promise resolving to an array of matching readings.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP POST request to `date`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getReadingsByDate(readings: ReadingsModel) {
    return new Promise<Array<ReadingsModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<ReadingsModel>>(`${this.baseUrl}${this.serviceUrl}date`, readings, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getPHSummary
   * @async
   * @description Fetch pH parameter summary for specific farm sites.
   *
   * @param {FarmSiteModel[]} farmsites - Array of farm site models to query.
   * @returns {Promise<Array<ReadingsModel>>} Promise resolving to the pH summary data.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP POST request to `pH/summary/farmSite`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getPHSummary(farmsites: FarmSiteModel[]) {
    return new Promise<Array<ReadingsModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<ReadingsModel>>(`${this.baseUrl}${this.serviceUrl}pH/summary/farmSite`, {
        farmSite: farmsites
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getSalinitySummary
   * @async
   * @description Fetch Salinity parameter summary for specific farm sites.
   *
   * @param {FarmSiteModel[]} farmsites - Array of farm site models to query.
   * @returns {Promise<Array<ReadingsModel>>} Promise resolving to the Salinity summary data.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP POST request to `salinity/summary/farmSite`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getSalinitySummary(farmsites: FarmSiteModel[]) {
    return new Promise<Array<ReadingsModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<ReadingsModel>>(`${this.baseUrl}${this.serviceUrl}salinity/summary/farmSite`, {
        farmSite: farmsites
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getDOTwoSummary
   * @async
   * @description Fetch Dissolved Oxygen (DO) parameter summary for specific farm sites.
   *
   * @param {FarmSiteModel[]} farmsites - Array of farm site models to query.
   * @returns {Promise<Array<ReadingsModel>>} Promise resolving to the DO summary data.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP POST request to `doTwo/summary/farmSite`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getDOTwoSummary(farmsites: FarmSiteModel[]) {
    return new Promise<Array<ReadingsModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<ReadingsModel>>(`${this.baseUrl}${this.serviceUrl}doTwo/summary/farmSite`, {
        farmSite: farmsites
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getTemperatureSummary
   * @async
   * @description Fetch Temperature parameter summary for specific farm sites.
   *
   * @param {FarmSiteModel[]} farmsites - Array of farm site models to query.
   * @returns {Promise<Array<ReadingsModel>>} Promise resolving to the Temperature summary data.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP POST request to `temperature/summary/farmSite`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getTemperatureSummary(farmsites: FarmSiteModel[]) {
    return new Promise<Array<ReadingsModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<ReadingsModel>>(`${this.baseUrl}${this.serviceUrl}temperature/summary/farmSite`, {
        farmSite: farmsites
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getAlkalinitySummary
   * @async
   * @description Fetch Alkalinity parameter summary for specific farm sites.
   *
   * @param {FarmSiteModel[]} farmsites - Array of farm site models to query.
   * @returns {Promise<Array<ReadingsModel>>} Promise resolving to the Alkalinity summary data.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP POST request to `alkalinity/summary/farmSite`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getAlkalinitySummary(farmsites: FarmSiteModel[]) {
    return new Promise<Array<ReadingsModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<ReadingsModel>>(`${this.baseUrl}${this.serviceUrl}alkalinity/summary/farmSite`, {
        farmSite: farmsites
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getTurbiditySummary
   * @async
   * @description Fetch Turbidity parameter summary for specific farm sites.
   *
   * @param {FarmSiteModel[]} farmsites - Array of farm site models to query.
   * @returns {Promise<Array<ReadingsModel>>} Promise resolving to the Turbidity summary data.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP POST request to `turbidity/summary/farmSite`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getTurbiditySummary(farmsites: FarmSiteModel[]) {
    return new Promise<Array<ReadingsModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<ReadingsModel>>(`${this.baseUrl}${this.serviceUrl}turbidity/summary/farmSite`, {
        farmSite: farmsites
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getPHLocation
   * @async
   * @description Fetch pH data grouped by location for specific farm sites.
   *
   * @param {FarmSiteModel[]} farmsites - Array of farm site models to query.
   * @returns {Promise<Array<ReadingsModel>>} Promise resolving to the pH location data.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP POST request to `pH/location/farmSite`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getPHLocation(farmsites: FarmSiteModel[]) {
    return new Promise<Array<ReadingsModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<ReadingsModel>>(`${this.baseUrl}${this.serviceUrl}pH/location/farmSite`, {
        farmSite: farmsites
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getSalinityLocation
   * @async
   * @description Fetch Salinity data grouped by location for specific farm sites.
   *
   * @param {FarmSiteModel[]} farmsites - Array of farm site models to query.
   * @returns {Promise<Array<ReadingsModel>>} Promise resolving to the Salinity location data.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP POST request to `salinity/location/farmSite`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getSalinityLocation(farmsites: FarmSiteModel[]) {
    return new Promise<Array<ReadingsModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<ReadingsModel>>(`${this.baseUrl}${this.serviceUrl}salinity/location/farmSite`, {
        farmSite: farmsites
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getDOTwoLocation
   * @async
   * @description Fetch Dissolved Oxygen (DO) data grouped by location for specific farm sites.
   *
   * @param {FarmSiteModel[]} farmsites - Array of farm site models to query.
   * @returns {Promise<Array<ReadingsModel>>} Promise resolving to the DO location data.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP POST request to `doTwo/location/farmSite`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getDOTwoLocation(farmsites: FarmSiteModel[]) {
    return new Promise<Array<ReadingsModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<ReadingsModel>>(`${this.baseUrl}${this.serviceUrl}doTwo/location/farmSite`, {
        farmSite: farmsites
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getTemperatureLocation
   * @async
   * @description Fetch Temperature data grouped by location for specific farm sites.
   *
   * @param {FarmSiteModel[]} farmsites - Array of farm site models to query.
   * @returns {Promise<Array<ReadingsModel>>} Promise resolving to the Temperature location data.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP POST request to `temperature/location/farmSite`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getTemperatureLocation(farmsites: FarmSiteModel[]) {
    return new Promise<Array<ReadingsModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<ReadingsModel>>(`${this.baseUrl}${this.serviceUrl}temperature/location/farmSite`, {
        farmSite: farmsites
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getAlkalinityLocation
   * @async
   * @description Fetch Alkalinity data grouped by location for specific farm sites.
   *
   * @param {FarmSiteModel[]} farmsites - Array of farm site models to query.
   * @returns {Promise<Array<ReadingsModel>>} Promise resolving to the Alkalinity location data.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP POST request to `alkalinity/location/farmSite`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getAlkalinityLocation(farmsites: FarmSiteModel[]) {
    return new Promise<Array<ReadingsModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<ReadingsModel>>(`${this.baseUrl}${this.serviceUrl}alkalinity/location/farmSite`, {
        farmSite: farmsites
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getTurbidityLocation
   * @async
   * @description Fetch Turbidity data grouped by location for specific farm sites.
   *
   * @param {FarmSiteModel[]} farmsites - Array of farm site models to query.
   * @returns {Promise<Array<ReadingsModel>>} Promise resolving to the Turbidity location data.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP POST request to `turbidity/location/farmSite`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getTurbidityLocation(farmsites: FarmSiteModel[]) {
    return new Promise<Array<ReadingsModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<ReadingsModel>>(`${this.baseUrl}${this.serviceUrl}turbidity/location/farmSite`, {
        farmSite: farmsites
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method getWaterQualitydetails
   * @async
   * @description Fetch a comprehensive summary of water quality details for specific farm sites.
   *
   * @param {FarmSiteModel[]} farmsites - Array of farm site models to query.
   * @returns {Promise<Array<ReadingsModel>>} Promise resolving to the water quality summary data.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP POST request to `pondName/sectionName/summary/farmSite`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public getWaterQualitydetails(farmsites: FarmSiteModel[]) {
    return new Promise<Array<ReadingsModel>>((resolve, reject) => {
      const request = this.httpClient.post<Array<ReadingsModel>>(`${this.baseUrl}${this.serviceUrl}pondName/sectionName/summary/farmSite`, {
        farmSite: farmsites
      }, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * This method iterates through the `ongoingRequests` array and calls `unsubscribe()`
   * on each subscription. This is typically called when a component using this service
   * is destroyed (ngOnDestroy) to ensure that no network requests continue in the background.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests tracked in `ongoingRequests`.
   * - Resets `ongoingRequests` to an empty array.
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
